import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress, Typography, TextField, Button, FormHelperText } from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone'; // Import timezone plugin
import utc from 'dayjs/plugin/utc'; // Import utc plugin to handle UTC

const BrokerCommission = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  // States for broker data, selected broker, and form data
  const [brokers, setBrokers] = useState([]);
  const [brokerId, setBrokerId] = useState('');
  const [formData, setFormData] = useState({ brokerComission: '' });
  const [loading, setLoading] = useState(false);
  const [commissionLoading, setCommissionLoading] = useState(false); // State for commission loading
  const [error, setError] = useState(null);

  const [selectedClients, setSelectedClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientCommissions, setClientCommissions] = useState({});
  const loggedInUserBrokerID = localStorage.getItem('brokerID');
  const doNotPerformCustomerFiltration = localStorage.getItem('role') === 'Super Admin' || localStorage.getItem('role') === 'Employee';
  const [effectiveDates, setEffectiveDates] = React.useState({});
  const [commissionEffectiveDates, setCommissionEffectiveDates] = React.useState({});
  const [effectiveDateErrorText, setEffectiveDateErrorText] = React.useState('');
  const [commissionEffectiveDateErrorText, setCommissionEffectiveDateErrorText] = React.useState('');

  dayjs.extend(timezone);
  dayjs.extend(utc);

  const handleEffectiveDateChange = (clientId, date) => {
    const dateInCST = dayjs(date).tz('America/Chicago', true);
    setEffectiveDates(prev => ({
      ...prev,
      [clientId]: dateInCST.format('YYYY-MM-DD'), // Format for backend
    }));
  };

  const handleCommissionEffectiveDateChange = (clientId, date) => {
    const dateInCST = dayjs(date).tz('America/Chicago', true);
    setCommissionEffectiveDates(prev => ({
      ...prev,
      [clientId]: dateInCST.format('YYYY-MM-DD'), // Format for backend
    }));
  };

  // Fetch brokers when the component mounts
  useEffect(() => {
    console.log('Broker ID: ', loggedInUserBrokerID);

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch brokers
        const brokersResponse = await fetch(`${BACKEND_URL}/v1/brokers`);
        if (!brokersResponse.ok) {
          const errorMessage = await brokersResponse.text();
          throw new Error(`Error fetching brokers: ${brokersResponse.status} ${brokersResponse.statusText} - ${errorMessage}`);
        }
        const brokersData = await brokersResponse.json();
        // setBrokers(brokersData);

        const filteredBrokers = doNotPerformCustomerFiltration
          ? brokersData.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
          : brokersData.filter(broker => broker.id === loggedInUserBrokerID).sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));

        setBrokers(filteredBrokers);

        // Fetch clients
        const clientsResponse = await fetch(`${BACKEND_URL}/v1/client`);
        if (!clientsResponse.ok) {
          const errorMessage = await clientsResponse.text();
          throw new Error(`Error fetching clients: ${clientsResponse.status} ${clientsResponse.statusText} - ${errorMessage}`);
        }
        let clientsData = await clientsResponse.json();
        // Filter out clientID "100" and sort by clientID (as string or number depending on data)
        clientsData = clientsData
          .filter(client => client.clientID !== '100')
          .sort((a, b) => {
            // Convert to number if clientID is numeric, else use localeCompare for string
            const aId = isNaN(a.clientID) ? a.clientID : Number(a.clientID);
            const bId = isNaN(b.clientID) ? b.clientID : Number(b.clientID);
            return typeof aId === 'number' ? aId - bId : aId.localeCompare(bId);
          });
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const sharedFormStyles = {
    '& .MuiInputBase-root': {
      height: '32px', // Match TextField height
      minHeight: '32px',
    },
    '& .MuiInputBase-input': {
      fontSize: '11px', // Match font size
      padding: '4px 8px',
    },
  };

  // Fetch commission based on selected broker
  useEffect(() => {
    if (!brokerId) return; // If no broker is selected, do not fetch commission

    const fetchCommission = async () => {
      setCommissionLoading(true);
      try {
        // Fetch the broker data, including the commission
        const response = await fetch(`${BACKEND_URL}/v1/brokers/${brokerId}`); // Replace with the correct endpoint for a specific broker
        const data = await response.json();
        setFormData(prevData => ({
          ...prevData,
          brokerComission: data.brokerComission,
        })); // Set the commission in form data
        setCommissionLoading(false);
      } catch (err) {
        setError(err.message);
        setCommissionLoading(false);
      }
    };

    fetchCommission();
  }, [brokerId]); // This effect runs whenever brokerId changes

  const fetchBrokers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/v1/brokers`);
      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error fetching brokers: ${response.status} ${response.statusText} - ${errorMessage}`);
      }
      const data = await response.json();

      // Apply filtration if required
      const filteredBrokers = doNotPerformCustomerFiltration ? data : data.filter(broker => broker.id === loggedInUserBrokerID);

      // Sort brokers by ID in ascending order

      const sortedBrokers = filteredBrokers.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));


      setBrokers(sortedBrokers);
      // setBrokers(data);
    } catch (error) {
      console.error('Error fetching brokers:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = event => {
    const newSelected = event.target.value;

    // Keep existing commissions
    const updatedCommissions = { ...clientCommissions };
    newSelected.forEach(clientID => {
      if (!updatedCommissions[clientID]) {
        updatedCommissions[clientID] = '0.00'; // Default value
      }
    });

    setSelectedClients(newSelected);
    setClientCommissions(updatedCommissions);
  };

  const handleBrokerChange = async brokerId => {
    setBrokerId(brokerId);
    const broker = brokers.find(b => b.id === brokerId);
    if (broker) {
      const assignedClients = broker.clients || [];

      // Extract assigned client IDs
      const assignedClientIDs = assignedClients.map(c => c.clientID);

      // Extract commission map from the broker's clients
      const commissionMap = {};
      const effectiveDateMap = {};
      const commissionEffectiveDateMap = {};
      assignedClients.forEach(c => {
        commissionMap[c.clientID] = c.commission.toFixed(2); // Convert to percentage
        if (c.effectiveDate) {
          effectiveDateMap[c.clientID] = c.effectiveDate;
        }
        if (c.commissionEffectiveDate) {
          commissionEffectiveDateMap[c.clientID] = c.commissionEffectiveDate;
        }
      });

      setSelectedClients(assignedClientIDs);
      setClientCommissions(commissionMap);
      setEffectiveDates(effectiveDateMap);
      setCommissionEffectiveDates(commissionEffectiveDateMap);
    }
  };

  const handleCommissionChange = (clientID, value) => {
    setClientCommissions(prev => ({
      ...prev,
      [clientID]: value,
    }));
  };

  const handleAssignClient = async () => {
    if (!brokerId || selectedClients.length === 0) {
      alert('Please select both a broker and at least one client.');
      return;
    }

    const invalidClients = selectedClients.filter(clientId => {
      const commission = parseFloat(clientCommissions[clientId]);
      const hasValidCommission = !isNaN(commission) && commission > 0;
      const hasEffectiveDate = effectiveDates[clientId];
      const hasCommissionEffectiveDate = commissionEffectiveDates[clientId];
      return !hasValidCommission || !hasEffectiveDate || !hasCommissionEffectiveDate;
    });

    if (invalidClients.length > 0) {
      const message = invalidClients
        .map(id => {
          const commission = parseFloat(clientCommissions[id]);
          const hasValidCommission = !isNaN(commission) && commission > 0;
          const hasEffectiveDate = effectiveDates[id];
          const hasCommissionEffectiveDate = commissionEffectiveDates[id];

          const issues = [];
          if (!hasValidCommission) issues.push('invalid commission');
          if (!hasEffectiveDate) issues.push('missing date');
          if (!hasCommissionEffectiveDate) issues.push('missing date');

          return `• Client ${id}: ${issues.join(', ')}`;
        })
        .join('\n');

      alert(`Please fix the following client inputs:\n${message}`);
      return;
    }

    try {
      const brokersResponse = await fetch(`${BACKEND_URL}/v1/brokers`);
      const allBrokers = await brokersResponse.json();

      const assignedToOtherBroker = selectedClients.filter(clientId => allBrokers.some(broker => broker.id !== brokerId && Array.isArray(broker.clients) && broker.clients.some(client => client.clientID === clientId)));

      if (assignedToOtherBroker.length > 0) {
        alert('One or more clients are already assigned to another broker!');
        return;
      }

      const clientAssignmentMap = selectedClients.reduce((map, clientId) => {
        const commission = parseFloat(clientCommissions[clientId]);
        const commissionEffectiveDate = commissionEffectiveDates[clientId];
        if (!isNaN(commission) && commission > 0 && commissionEffectiveDate) {
          map[clientId] = {
            commission,
            commissionEffectiveDate,
          };
        }
        return map;
      }, {});

      const payload = { clientAssignmentMap };

      console.log('Payload for backend:', payload);

      const response = await fetch(`${BACKEND_URL}/v1/broker/${brokerId}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        throw new Error(`Error assigning clients: ${response.status} ${response.statusText} - ${errorMessage}`);
      }

      await fetchBrokers();
      alert('Commission updated successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Error assigning clients:', error);
      setError(error.message);
      alert('An error occurred while assigning the clients.');
    }
  };

  return (
    <div style={{ marginLeft: '10px', marginTop: '40px' }}>
      <h1
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          marginTop: '-33px',
          textTransform: 'uppercase',
          fontFamily: 'sans-serif',
        }}
      >
        Broker Commission
      </h1>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" sx={sharedFormStyles}>
            <InputLabel id="brokerDropdown">Select Broker</InputLabel>
            {loading ? (
              <CircularProgress
                size={24}
                sx={{
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  display: 'block',
                  textTransform: 'uppercase',
                }}
              />
            ) : error ? (
              <Typography color="error" align="center">
                {`Error: ${error}`}
              </Typography>
            ) : (
              <Select
                labelId="brokerDropdown"
                id="brokerDropdown"
                value={brokerId}
                label="Select Broker"
                onChange={e => handleBrokerChange(e.target.value)}
                disabled={brokers.length === 0}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '200px',
                      minHeight: '100px',
                      textTransform: 'uppercase',
                      '& .MuiMenuItem-root': {
                        fontSize: '10px',
                        marginLeft: '5px',
                        padding: '10px 15px',
                      },
                    },
                  },
                }}
              >
                {brokers.map(broker => (
                  <MenuItem key={broker.id} value={broker.id}>
                    {`${broker.id} - ${broker.brokerName}`}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" sx={sharedFormStyles}>
            <InputLabel
              id="clientDropdown"
              sx={{
                fontSize: '15px',
                // top: '-20%', // Initially center the label vertically
              }}
            >
              Select Client
            </InputLabel>
            {loading ? (
              <CircularProgress
                size={24}
                sx={{
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  display: 'block',
                }}
              />
            ) : error ? (
              <Typography color="error" align="center">
                {`Error: ${error}`}
              </Typography>
            ) : (
              <Select
                labelId="clientDropdown"
                id="clientDropdown"
                value={selectedClients}
                label="Select Client"
                multiple
                onChange={handleSelectChange}
                disabled
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '200px',
                      minHeight: '100px',
                      '& .MuiMenuItem-root': {
                        fontSize: '10px',
                        marginLeft: '5px',
                        padding: '5px 15px',
                      },
                    },
                  },
                }}
              >
                {clients.map(client => (
                  <MenuItem key={client.clientID} value={client.clientID}>
                    {`${client.clientID} - ${client.clientName}`}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          {selectedClients.length > 0 && (
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '14px',
                fontWeight: 'bold',
                fontFamily: 'sans-serif',
                paddingBottom: '15px',
              }}
            >
              SELECTED CLIENTS:
            </Typography>
          )}
          {/* <div>
                        {selectedClients.map(clientID => {
                          const client = clients.find(client => client.clientID === clientID);
                          return (
                            client && (
                              <Chip
                                key={client.clientID}
                                label={`${client.clientID} - ${client.clientName}`}
                                sx={{ margin: '4px' }}
                              />
                            )
                          );
                        })}
                      </div> */}

          <Grid container spacing={2}>
            {selectedClients.map(clientID => {
              const client = clients.find(c => c.clientID === clientID);
              return (
                client && (
                  <React.Fragment key={client.clientID}>
                    <Grid item xs={4}>
                      <TextField
                        label="Client Name"
                        value={`${client.clientID} - ${client.clientName}`}
                        fullWidth
                        size="small"
                        disabled
                        InputProps={{
                          sx: { fontSize: 15 },
                        }}
                        inputProps={{
                          sx: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: 10,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <FormControl
                        // sx={{ minWidth: '100%' }}
                        size="small"
                        error={effectiveDateErrorText}
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            placeholder="Effective Date" // Use placeholder instead of label
                            label="Client Effective Date"
                            // value={dayjs(effectiveDates[clientID])}
                            value={effectiveDates[clientID] && dayjs(effectiveDates[clientID]).isValid() ? dayjs(effectiveDates[clientID]) : null}
                            onChange={date => handleEffectiveDateChange(clientID, date)}
                            size="small"
                            disabled={!!effectiveDates[clientID]} // Disable if value exists
                            slotProps={{
                              textField: {
                                InputLabelProps: {
                                  sx: effectiveDates[clientID] ? { fontSize: '13px' } : { fontSize: '10px', top: '-9px' },
                                },
                                sx: {
                                  '& .MuiInputBase-root': {
                                    height: 30, // Increased height for better centering
                                    minHeight: 30,
                                    // width: '150px',
                                    // paddingRight: '28px', // Space for the icon
                                    display: 'flex',
                                    alignItems: 'center', // Ensures vertical centering
                                  },
                                  '& .MuiInputBase-input': {
                                    fontSize: 11,
                                    textAlign: 'left', // Centers text horizontally
                                    padding: '8px 5px', // Centers text vertically
                                  },
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 12, // Reduce icon size
                                    right: '8px', // Position the icon
                                  },
                                },
                              },
                            }}
                            // onChange={handleEffectiveDateChange}
                            // shouldDisableDate={(date) => {
                            //   const day = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                            //   const today = dayjs().startOf('day'); // Get today without time
                            //   return date.isBefore(today); // Allow only Monday (1) & Thursday (4) and disable past dates
                            // }}
                          />
                        </LocalizationProvider>

                        {effectiveDateErrorText && <FormHelperText id="input-error-text">{effectiveDateErrorText}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item xs={3}>
                      <TextField
                        label="Commission (%)"
                        type="number"
                        value={clientCommissions[clientID] || '0.0000'}
                        // onChange={e => handleCommissionChange(clientID, e.target.value)}
                        onChange={e => {
                          const value = parseFloat(e.target.value);
                          const safeValue = isNaN(value) || value < 0 ? 0.0000 : value;
                          handleCommissionChange(clientID, safeValue.toFixed(4));
                        }}
                        fullWidth
                        size="small"
                        inputProps={{
                          step: '0.0001',
                          min: '0.0000',
                          style: { fontSize: 11 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <FormControl sx={{ minWidth: '100%' }} size="small" error={commissionEffectiveDateErrorText}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            placeholder="Commission Effective Date" // Use placeholder instead of label
                            label="Commission Effective Date"
                            // value={dayjs(commissionEffectiveDates[clientID])}
                            value={commissionEffectiveDates[clientID] && dayjs(commissionEffectiveDates[clientID]).isValid() ? dayjs(commissionEffectiveDates[clientID]) : null}
                            onChange={date => handleCommissionEffectiveDateChange(clientID, date)}
                            size="small"
                            slotProps={{
                              textField: {
                                InputLabelProps: {
                                  sx: commissionEffectiveDates[clientID] ? { fontSize: '13px' } : { fontSize: '10px', top: '-9px' },
                                },
                                sx: {
                                  '& .MuiInputBase-root': {
                                    height: 30, // Increased height for better centering
                                    minHeight: 30,
                                    width: '160px',
                                    // paddingRight: '48px', // Space for the icon
                                    display: 'flex',
                                    alignItems: 'center', // Ensures vertical centering
                                  },
                                  '& .MuiInputBase-input': {
                                    fontSize: 11,
                                    textAlign: 'left', // Centers text horizontally
                                    padding: '8px 5px', // Centers text vertically
                                  },
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 12, // Reduce icon size
                                    // right: '8px', // Position the icon
                                  },
                                },
                              },
                            }}
                            // onChange={handleEffectiveDateChange}
                            // shouldDisableDate={(date) => {
                            //   const day = date.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                            //   const today = dayjs().startOf('day'); // Get today without time
                            //   return date.isBefore(today); // Allow only Monday (1) & Thursday (4) and disable past dates
                            // }}
                          />
                        </LocalizationProvider>

                        {commissionEffectiveDateErrorText && <FormHelperText id="input-error-text">{commissionEffectiveDateErrorText}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    {/* <Grid item xs={2}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveClient(clientID)}
                                    sx={{ marginTop: '4px' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Grid> */}
                  </React.Fragment>
                )
              );
            })}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Button
            style={{
              marginTop: '15px',
              fontFamily: 'sans-serif',
              fontSize: '10px',
              backgroundColor: '#31478F',
              marginLeft: '190px',
            }}
            variant="contained"
            onClick={handleAssignClient}
            sx={{
              '&:hover': {
                backgroundColor: '#31478F', // Prevent hover effect by keeping the same color
              },
            }}
          >
            Update Client Commission
          </Button>
          <Button
            variant="outlined"
            size="small"
            // onClick={() => setFormVisible(false)}
            onClick={() => {
              // setFormVisible(false);
              setSelectedClients([]); // Clear selected clients
              setClientCommissions({}); // Clear commission values
              navigate('/manage-brokers/list');
            }}
            sx={{
              mt: 2,
              ml: 2,
              backgroundColor: '#E8F0FE',
              marginTop: '15px',
            }}
          >
            Go Back
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default BrokerCommission;
